import { useCallback } from 'react'
import { GridApi } from 'ag-grid-enterprise'
import { WithBalanceRegistries } from '../../features/financialStatements/types'
import { hasRowUnsavedChanges, discardRowChanges } from '../utils/balanceRegistry'

interface UseGridDataActionsProps<T extends WithBalanceRegistries> {
  gridApi?: GridApi<T>
}

interface UseGridDataActionsReturn<T extends WithBalanceRegistries> {
  hasUnsavedChanges: () => boolean
  discardChanges: () => number
  changedRowsCount: () => number
}

/**
 * Generic hook for grid data table actions (save, discard, etc.)
 * Works with any data type that implements WithBalanceRegistries interface
 *
 * @example
 * ```tsx
 * // For Driver Data
 * const { hasUnsavedChanges, discardChanges } = useGridDataActions<DriverData>({ gridApi })
 *
 * // For Income Statement
 * const { hasUnsavedChanges, discardChanges } = useGridDataActions<EnhancedStatementRow>({ gridApi })
 * ```
 */
export const useGridDataActions = <T extends WithBalanceRegistries>({
  gridApi
}: UseGridDataActionsProps<T>): UseGridDataActionsReturn<T> => {
  /**
   * Checks if there are any unsaved changes in the grid
   * Lazy evaluation - only computed when called
   */
  const hasUnsavedChanges = useCallback((): boolean => {
    if (!gridApi) return false

    let hasChanges = false
    gridApi.forEachNode(node => {
      if (node.data && hasRowUnsavedChanges(node.data)) {
        hasChanges = true
      }
    })

    return hasChanges
  }, [gridApi])

  /**
   * Gets the count of rows with unsaved changes
   * Lazy evaluation - only computed when called
   */
  const changedRowsCount = useCallback((): number => {
    if (!gridApi) return 0

    let count = 0
    gridApi.forEachNode(node => {
      if (node.data && hasRowUnsavedChanges(node.data)) {
        count += 1
      }
    })

    return count
  }, [gridApi])

  /**
   * Discards all unsaved changes
   * @returns The number of rows that had changes
   */
  const discardChanges = useCallback((): number => {
    if (!gridApi) return 0

    console.log('Discarding all changes...')

    // Clear changes from ALL rows (no need to check - delete is safe even if properties don't exist)
    gridApi.forEachNode(node => {
      if (node.data) {
        discardRowChanges(node.data)
      }
    })

    // Redraw rows to show original values
    gridApi.refreshCells({ force: true })

    return 0 // Count doesn't matter anymore
  }, [gridApi])

  return {
    hasUnsavedChanges,
    changedRowsCount,
    discardChanges
  }
}
